
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../App.jsx';
import { COLORS, USERS_COLLECTION, CHATS_COLLECTION } from '../../config/constants';
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { Calendar, CheckCircle, Clock, MapPin, HelpCircle } from 'lucide-react';
import ClientChatCard from '../../components/servi/ClientChatCard.jsx';
import AuthLoadingScreen from '../AuthLoadingScreen.jsx';
import StatusSelectionModal from '../../components/servi/StatusSelectionModal.jsx';
import TerviLogo from '../../components/common/TerviLogo.jsx';
import ServiceRequestModal from '../../components/servi/ServiceRequestModal.jsx';

const CHATS_PER_PAGE = 12;

const ServiDashboard = () => {
    const { user, db, updateUserProfile, scheduleService, fullUserData } = useAppContext();
    const navigate = useNavigate();

    const [allChats, setAllChats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [activeFilter, setActiveFilter] = useState('Todos');
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const helpRef = useRef(null);
    const listContainerRef = useRef(null);

    const filterOptions = ['Todos', 'Novo', 'Em Progresso'];

    const fetchChats = useCallback(async (loadMore = false) => {
        if (!db || !user) return;

        if (!loadMore) {
            setIsLoading(true);
            setAllChats([]);
            setLastVisible(null);
            setHasMore(true);
        }

        if (loadMore && !lastVisible) {
            setHasMore(false);
            return;
        }

        setIsLoadingMore(loadMore);

        try {
            let q = query(
                collection(db, CHATS_COLLECTION),
                where("participants", "array-contains", user.uid),
                orderBy('lastMessageTimestamp', 'desc'),
                limit(CHATS_PER_PAGE)
            );

            if (loadMore && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }

            const documentSnapshots = await getDocs(q);
            const newChats = documentSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            setLastVisible(documentSnapshots.docs[documentSnapshots.docs.length - 1]);
            setAllChats(prev => loadMore ? [...prev, ...newChats] : newChats);
            setHasMore(newChats.length === CHATS_PER_PAGE);

        } catch (error) {
            console.error("Erro ao buscar chats do SERVI:", error);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [db, user, lastVisible, hasMore]);

    useEffect(() => {
        fetchChats(false);
    }, [db, user]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (helpRef.current && !helpRef.current.contains(event.target)) {
                setIsHelpModalOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredChats = useMemo(() => {
        if (!user) return [];
        const finishedStatus = ['Finalizado', 'Cancelado', 'Orçamento Recusado', 'Pago'];
        const userChats = allChats.filter(chat => chat.serviId === user.uid);

        switch (activeFilter) {
            case 'Novo': return userChats.filter(chat => chat.status === 'Novo');
            case 'Em Progresso': return userChats.filter(chat => !finishedStatus.includes(chat.status) && chat.status !== 'Novo');
            case 'Todos':
            default: return userChats.filter(chat => !finishedStatus.includes(chat.status));
        }
    }, [allChats, user, activeFilter]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop < clientHeight * 1.5 && hasMore && !isLoadingMore) {
            fetchChats(true);
        }
    };

    const serviProfile = useMemo(() => fullUserData?.profile_servi, [fullUserData]);
    const handleStatusChange = (newStatus) => { if (user && serviProfile) { updateUserProfile(user.uid, 'servi', { ...serviProfile, status: newStatus }); } };
    const handleGoToChat = () => { if (selectedRequest) { navigate(`/servi/chat/${selectedRequest.id}`); } setSelectedRequest(null); };

    const statusConfig = { 'Disponível': { color: COLORS.SUCCESS_GREEN, Icon: CheckCircle }, 'Agendamento': { color: COLORS.PRIMARY_BLUE, Icon: Calendar }, 'Indisponível': { color: '#6B7280', Icon: Clock } };
    const currentStatusConfig = statusConfig[serviProfile?.status] || statusConfig['Indisponível'];
    const companyName = serviProfile?.companyName || serviProfile?.name || 'Prestador';
    const serviCity = serviProfile?.city || 'Localização não definida';

    const truncateName = (name, maxLength = 15) => name.length <= maxLength ? name : `${name.substring(0, maxLength)}...`;
    const displayName = truncateName(companyName);

    if (isLoading && allChats.length === 0) return <AuthLoadingScreen message="Carregando seu dashboard..." />;

    return (
        <div className="h-full flex flex-col bg-gray-100">
            <header className="bg-white shadow-lg p-3 z-10">
                 <div className="flex justify-between items-center">
                    <div><TerviLogo size={28} /></div>
                    {serviProfile && (
                        <div className="flex items-center text-right">
                             <div className="max-w-[calc(100vw-150px)]">
                                <h2 className="font-bold text-gray-800 text-sm leading-tight">{displayName}</h2>
                                <p className="text-xs text-gray-500 flex items-center justify-end"><MapPin className="w-3 h-3 mr-1 flex-shrink-0" /> <span className="truncate">{serviCity}</span></p>
                             </div>

                             {/* FOTO SQUIRCLE NO DASHBOARD */}
                             <div className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-blue-100 shadow-sm flex items-center justify-center p-0.5 ml-3 flex-shrink-0">
                                <img src={serviProfile.photoUrl || `https://ui-avatars.com/api/?name=${companyName.replace(/ /g, '+')}&background=0D8ABC&color=fff`} alt={companyName} className="w-full h-full object-contain rounded-lg"/>
                             </div>
                        </div>
                    )}
                </div>
            </header>

            <div className="p-4 bg-white border-b">
                {serviProfile && (
                     <div className="flex items-stretch rounded-xl shadow-lg overflow-hidden">
                         <div className="bg-gray-200 px-4 flex items-center"><h2 className="text-sm font-semibold text-gray-700">MEU STATUS ATUAL</h2></div>
                         <button onClick={() => setIsStatusModalOpen(true)} className="flex-grow flex items-center justify-center p-3 text-lg font-bold transition transform active:scale-95 text-white" style={{ backgroundColor: currentStatusConfig.color }}>
                            {currentStatusConfig.Icon && <currentStatusConfig.Icon className="w-6 h-6 mr-3" />}
                            {serviProfile?.status || 'Indisponível'}
                        </button>
                    </div>
                )}
            </div>

            <div ref={listContainerRef} onScroll={handleScroll} className="flex-grow overflow-y-auto pb-20">
                <div className="p-4 sticky top-0 bg-gray-100 z-10 border-t">
                     <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3 overflow-x-auto pb-2">
                            {filterOptions.map(status => <button key={status} onClick={() => setActiveFilter(status)} className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${activeFilter === status ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-700'}`}>{status}</button>)}
                        </div>
                        <div className="relative ml-4" ref={helpRef}>
                             <button onClick={() => setIsHelpModalOpen(prev => !prev)} className="p-2 rounded-full hover:bg-gray-200"><HelpCircle className="w-5 h-5 text-gray-600" /></button>
                             {isHelpModalOpen && (
                                 <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl z-20 border p-4">
                                    <h3 className="font-bold text-gray-800 mb-3 text-base">Função dos Status</h3>
                                    <ul className="space-y-3 text-sm">
                                        <li className="flex items-start"><div className="w-3 h-3 rounded-full mt-1 mr-3 flex-shrink-0" style={{backgroundColor: COLORS.SUCCESS_GREEN}}></div><div><strong className="font-semibold text-gray-700">Disponível:</strong> Informa ao cliente a disponibilidade de serviço imediato.</div></li>
                                        <li className="flex items-start"><div className="w-3 h-3 rounded-full mt-1 mr-3 flex-shrink-0" style={{backgroundColor: COLORS.PRIMARY_BLUE}}></div><div><strong className="font-semibold text-gray-700">Agendamento:</strong> Informa que os serviços são realizados mediante agendamento prévio.</div></li>
                                        <li className="flex items-start"><div className="w-3 h-3 rounded-full bg-gray-500 mt-1 mr-3 flex-shrink-0"></div><div><strong className="font-semibold text-gray-700">Indisponível:</strong> Informa que não está aceitando novos serviços e desabilita novas solicitações para os clientes.</div></li>
                                    </ul>
                                 </div>
                             )}
                        </div>
                    </div>
                </div>

                <div className="px-4 pb-4 pt-2 space-y-4">
                    {filteredChats.length > 0
                        ? filteredChats.map(chat => <ClientChatCard key={chat.id} chat={chat} user={user} onClick={() => setSelectedRequest(chat)} />)
                        : <div className="text-center p-8 pt-16"><p className="text-gray-500">Nenhuma solicitação com o status '{activeFilter}'.</p></div>}
                    {isLoadingMore && <div className="text-center p-2 text-gray-500">Carregando mais...</div>}
                </div>
            </div>

            {isStatusModalOpen && <StatusSelectionModal currentStatus={serviProfile?.status} onClose={() => setIsStatusModalOpen(false)} onSelectStatus={handleStatusChange} />}
            {selectedRequest && <ServiceRequestModal chat={selectedRequest} user={user} onClose={() => setSelectedRequest(null)} onGoToChat={handleGoToChat} scheduleService={scheduleService} />}
        </div>
    );
};

export default ServiDashboard;
